import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react'
import { Button } from '@/components/common'
import Logo from '@/components/common/Logo'
import { toast } from 'react-toastify'
import { API_BASE_URL } from '@/constants'

export default function ForgotPasswordPage() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      })

      const data = await response.json()

      if (response.ok) {
        setSubmitted(true)
        toast.success('ส่งลิงก์รีเซ็ตรหัสผ่านไปยังอีเมล์ของคุณแล้ว')
      } else {
        toast.error(data.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง')
      }
    } catch (error) {
      console.error('Forgot password error:', error)
      toast.error('ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้')
    } finally {
      setLoading(false)
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-success-light rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-success" />
            </div>

            <h1 className="text-2xl font-bold text-text-primary mb-2">
              ตรวจสอบอีเมล์ของคุณ
            </h1>

            <p className="text-text-secondary mb-6">
              หากอีเมล์ <strong>{email}</strong> มีอยู่ในระบบ
              <br />
              เราได้ส่งลิงก์สำหรับรีเซ็ตรหัสผ่านไปแล้ว
            </p>

            <div className="bg-background-secondary rounded-lg p-4 mb-6 text-left">
              <h3 className="font-semibold text-text-primary mb-2">ขั้นตอนถัดไป:</h3>
              <ol className="list-decimal list-inside text-sm text-text-secondary space-y-1">
                <li>เปิดอีเมล์ของคุณ</li>
                <li>คลิกลิงก์ในอีเมล์</li>
                <li>ตั้งรหัสผ่านใหม่</li>
              </ol>
            </div>

            <p className="text-xs text-text-tertiary mb-6">
              ไม่ได้รับอีเมล์? ตรวจสอบโฟลเดอร์ Spam หรือ{' '}
              <button
                onClick={() => setSubmitted(false)}
                className="text-primary-800 hover:text-primary-900 font-medium"
              >
                ส่งใหม่อีกครั้ง
              </button>
            </p>

            <Button
              variant="secondary"
              onClick={() => navigate('/login')}
              className="w-full"
            >
              กลับไปหน้าเข้าสู่ระบบ
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <Logo size="lg" variant="full" showText={true} />
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-text-primary mb-2">ลืมรหัสผ่าน?</h1>
          <p className="text-text-secondary">
            กรอกอีเมล์ของคุณ เราจะส่งลิงก์สำหรับรีเซ็ตรหัสผ่านให้
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email Input */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
              อีเมล์
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full pl-10 pr-4 py-3 border border-border-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-800 focus:border-transparent"
                placeholder="your@email.com"
                disabled={loading}
              />
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            disabled={loading}
            className="w-full"
          >
            {loading ? 'กำลังส่ง...' : 'ส่งลิงก์รีเซ็ตรหัสผ่าน'}
          </Button>

          {/* Back to Login */}
          <div className="text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm text-primary-800 hover:text-primary-900 font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              กลับไปหน้าเข้าสู่ระบบ
            </Link>
          </div>
        </form>

        {/* Help Text */}
        <div className="mt-8 p-4 bg-background-secondary rounded-lg">
          <p className="text-xs text-text-secondary text-center">
            ต้องการความช่วยเหลือ?{' '}
            <a
              href="mailto:aisenovate@gmail.com"
              className="text-primary-800 hover:text-primary-900 font-medium"
            >
              ติดต่อเรา
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
